// Hardfin Confidential - Restricted

/**
 * API endpoint for toggling in-service date management mode.
 * Allows switching between automatic (Hardfin AI) and manual management.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { detailedAssetSchema } from '@/asset/api/types';
import { z } from 'zod';

/**
 * Schema for validating the API response when toggling management mode.
 */
const updateInServiceDateManagementResponseSchema = z
  .object({
    data: detailedAssetSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, asset: data }));

/**
 * Toggles the in-service date management mode for an asset.
 * When toggling to automatic (true), triggers a synchronous resync to compute the date.
 * When toggling to manual (false), simply sets the flag without recomputing.
 *
 * @param key - Unique identifier of the asset to update
 * @param automatic - Whether to enable automatic management (true) or manual management (false)
 * @returns Promise resolving to the updated asset data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function updateInServiceDateManagement(
  key: string,
  automatic: boolean
) {
  const url = `/v1/asset/${key}/accounting/in-service-management`;
  const response = await api.patch(url, { automatic });

  return parseResponse(
    updateInServiceDateManagementResponseSchema,
    response.data,
    {
      schemaName: 'UpdateInServiceDateManagement',
      endpoint: url,
    }
  );
}
